import { useEffect, useRef, useState } from 'preact/hooks';
import { findBooks, type FoundBook } from '../book/load';
import { bookLabel, statusName } from '../book/model';
import { navigate, useRoute } from '../lib/router';

// The header search (Kristi, Oct 26): finds books on your list by title, author or B-number
// and opens them. Only while the book page is switched on.

export function useFindBooks(text: string) {
  const [found, setFound] = useState<FoundBook[] | null>(null);
  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) {
      setFound(null);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      findBooks(q)
        .then((r) => live && setFound(r))
        .catch(() => live && setFound([]));
    }, 220);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [text]);
  return found;
}

/** Opens a book from the search: over the section you're in (laptop), keeping Back. */
export function useOpenBook() {
  const { path, state } = useRoute();
  return (label: number) => {
    const from = path.startsWith('/book/') ? state.from : path;
    navigate(`/book/${bookLabel(label)}`, { state: { from, full: state.full }, keepScroll: true });
  };
}

export function FindResults({
  text,
  found,
  active,
  onPick,
  id,
}: {
  text: string;
  found: FoundBook[] | null;
  active: number;
  onPick: (b: FoundBook) => void;
  id: string;
}) {
  if (text.trim().length < 2) return null;
  return (
    <div class="find-results">
      {found === null ? (
        <p class="find-note">Looking…</p>
      ) : found.length === 0 ? (
        <p class="find-note">No book on your list matches “{text.trim()}”.</p>
      ) : (
        <ul role="listbox" id={id} aria-label="Books on your list">
          {found.map((b, i) => (
            <li key={b.label} role="option" id={`${id}-${i}`} aria-selected={i === active}>
              <button type="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(b)}>
                <span class="find-title">{b.title}</span>
                <span class="find-meta">
                  {bookLabel(b.label)} · {statusName(b.status)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p class="find-note">Searching your list.</p>
    </div>
  );
}

/** The search box with its results (laptop header, and the phone's search sheet). */
export function FindBox({
  autoFocus,
  onDone,
  inputId,
  placeholder = 'Find a book on your list',
}: {
  autoFocus?: boolean;
  onDone?: () => void;
  inputId: string;
  placeholder?: string;
}) {
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const found = useFindBooks(text);
  const openBook = useOpenBook();
  const input = useRef<HTMLInputElement>(null);
  const listId = `${inputId}-list`;

  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, []);
  useEffect(() => setActive(0), [found]);

  const pick = (b: FoundBook) => {
    setText('');
    setOpen(false);
    input.current?.blur();
    onDone?.();
    openBook(b.label);
  };

  return (
    <div class="find">
      <form
        role="search"
        class="hdr-search"
        onSubmit={(e) => {
          e.preventDefault();
          const b = found?.[active];
          if (b) pick(b);
        }}
      >
        <label for={inputId} class="visually-hidden">
          Search
        </label>
        <input
          id={inputId}
          ref={input}
          type="search"
          autocomplete="off"
          placeholder={placeholder}
          value={text}
          aria-controls={listId}
          aria-activedescendant={found?.length ? `${listId}-${active}` : undefined}
          onInput={(e) => {
            setText((e.target as HTMLInputElement).value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (!found?.length) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => (a + 1) % found.length);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => (a - 1 + found.length) % found.length);
            } else if (e.key === 'Escape') {
              setOpen(false);
              onDone?.();
            }
          }}
        />
      </form>
      {open || onDone ? <FindResults text={text} found={found} active={active} onPick={pick} id={listId} /> : null}
    </div>
  );
}
